<template>
    <div v-if="visible" class="add-photos-modal-overlay" @click="closeModal">
        <div class="add-photos-modal" @click.stop>
            <div class="modal-header">
                <h3 class="modal-title">添加图片到 {{ albumName }}</h3>
                <button class="modal-close" @click="closeModal">
                    <i class="el-icon-close"></i>
                </button>
            </div>
            
            <div class="modal-body">
                <!-- 状态筛选按钮 -->
                <div class="status-filter-btns">
                    <button
                        v-for="item in categoryOptions"
                        :key="item.value" 
                        :class="['status-btn', { selected: selectedCategory === item.value }]"
                        @click="selectStatus(item.value)" 
                    >
                        {{ item.label }}
                    </button>
                </div>
                
                <!-- 图片列表 -->
                <ul 
                    class="photo-list" 
                    v-infinite-scroll="loadMore" 
                    :infinite-scroll-disabled="isLoading || isNoMore"
                    :infinite-scroll-distance="50"
                >
                    <li 
                        v-for="(item, i) in validPhotoList"
                        :key="i"
                        :class="['photo-item', { 'is-selected': item.isClicked }]" 
                        @click="toggleSelect(item)" 
                        @mouseenter="item.isHovered = true" 
                        @mouseleave="item.isHovered = false"
                        style="cursor: pointer;"
                    >
                        <div class="image-container">
                            <imagePreview 
                                :src="item.url" 
                                :preview-src="item.originalUrl"
                                :preview-src-list="previewImageList" 
                                :current-index="i"
                                :alt="item.originalName || '图片'" 
                                @click.stop 
                            />
                            
                            <!-- 标签图标 -->
                            <el-tooltip 
                                placement="top" 
                                effect="light" 
                                :content="getTagDescription(item)"
                                v-if="item && getIsShowTag(item) && getTagIcon(item)"
                            >
                                <div class="media-tag">
                                    <img :src="getTagIcon(item)" class="tag-icon" />
                                </div>
                            </el-tooltip>
                        </div>
                        
                        <!-- 选中状态指示 -->
                        <div class="selection-indicator" v-if="item.isClicked">
                            <i class="el-icon-success" style="color: #5FAAFF;"></i>
                        </div>
                    </li>
                    
                    <!-- 加载状态 -->
                    <li v-if="isLoading" class="loading-item">
                        <img class="loading-icon" src="@/assets/loading.gif" alt="">
                    </li>
                    
                    <!-- 无更多数据 -->
                    <li v-if="isNoMore && !isLoading" class="no-more-item">
                        没有更多了
                    </li>
                </ul>
                
                <!-- 空状态 -->
                <div v-if="!isLoading && photoList.length === 0" class="empty-state">
                    <i class="el-icon-picture" style="font-size: 48px; color: #c0c4cc;"></i>
                    <p>暂无图片</p>
                </div>
            </div>
            
            <div class="modal-footer">
                <div class="selected-info">
                    已选择 {{ selectedCount }} 张图片
                </div>
                <div class="action-buttons">
                    <el-button @click="closeModal" size="medium">取消</el-button>
                    <el-button 
                        type="primary" 
                        @click="confirmAdd" 
                        :loading="addingPhotos"
                        :disabled="selectedCount === 0"
                        size="medium"
                    >
                        添加 ({{ selectedCount }})
                    </el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getConnectionPhotos } from '@/api/talkytimes/message'
import { addPhotosToAlbum } from '@/api/talkytimes/attachment'
import imagePreview from '@/components/imagePreviewForAttachment.vue'

export default {
    name: 'AddPhotosModal',
    components: { imagePreview },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        albumId: {
            type: [String, Number],
            required: true
        },
        albumName: {
            type: String,
            required: true
        },
        chatUserInfo: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            photoList: [],
            selectedCategory: 'all_available',
            categoryOptions: [
                { label: 'All available', value: 'all_available' },
                { label: 'Basic', value: 'basic' },
                { label: 'Pre-special', value: 'pre_special' },
                { label: 'Special', value: 'special' },
                { label: 'Special +', value: 'special_plus' },
            ],
            limit: 50,
            cursor: '',
            isLoading: false,
            isNoMore: false,
            addingPhotos: false,
            previewImageList: []
        }
    },
    computed: {
        selectedCount() {
            return this.photoList.filter(item => item && item.isClicked).length;
        },
        validPhotoList() {
            return this.photoList.filter(item => item != null && item !== undefined);
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.loadPhotos();
            } else {
                this.resetData();
            }
        },
        selectedCategory() {
            this.refresh();
        }
    },
    methods: {
        // 加载照片数据
        async loadPhotos(isLoadMore = false) {
            if (this.isLoading) return;
            
            this.isLoading = true;
            
            let idInterlocutor = this.chatUserInfo.userId || this.chatUserInfo.idInterlocutor || this.chatUserInfo.id_user || this.chatUserInfo.id;
            
            // 获取自己的用户ID (pdUserId)
            const activeUser = localStorage.getItem('activeUser');
            const pdUserId = activeUser ? JSON.parse(activeUser).id : null;
            
            // 如果 idInterlocutor 无效，使用 pdUserId 作为后备
            if (!idInterlocutor || idInterlocutor === 'default') {
                idInterlocutor = pdUserId;
            }
            
            if (!idInterlocutor || idInterlocutor === 'default') {
                console.error('无法获取有效的用户ID');
                this.isLoading = false;
                this.$message.error('无法获取用户信息');
                return;
            }
            
            try {
                // 使用新的连接照片接口，构建请求参数
                const params = {
                    idInterlocutor: Number(idInterlocutor),
                    limit: this.limit,
                    statuses: ['approved', 'approved_by_ai']
                };
                
                // 添加pdUserId（如果有值）
                if (pdUserId) {
                    params.pdUserId = Number(pdUserId);
                }
                
                // 根据选择的分类构建请求参数
                if (this.selectedCategory === 'all_available') {
                    // 分类1: All available - 只有statuses和excludeConnectionStatuses
                } else if (this.selectedCategory === 'basic') {
                    // 分类2: Basic - excludeTags: ["pre_special", "special", "special_plus"]
                    params.excludeTags = ['pre_special', 'special', 'special_plus'];
                } else if (this.selectedCategory === 'pre_special') {
                    // 分类3: Pre-special - tags: ["pre_special"]
                    params.tags = ['pre_special'];
                } else if (this.selectedCategory === 'special') {
                    // 分类4: Special - tags: ["special"]
                    params.tags = ['special'];
                } else if (this.selectedCategory === 'special_plus') {
                    // 分类5: Special + - tags: ["special_plus"]
                    params.tags = ['special_plus'];
                }
                
                // 分页参数
                if (isLoadMore && this.cursor) {
                    params.cursor = String(this.cursor);
                }
                
                const res = await getConnectionPhotos(params);
                const photosData = res.data || res;
                const newPhotos = (photosData.photos || []).map(item => {
                    if (!item) return null;
                    // 处理 status 字段，确保兼容字符串和对象格式
                    let status = item.status;
                    if (!status) {
                        // 如果 status 不存在，根据 statuses 字段推断
                        status = item.statuses || 'approved';
                    }
                    if (typeof status !== 'string' && status && status.code) {
                        status = status.code;
                    }
                    
                    return {
                        ...item,
                        idPhoto: item.idPhoto || item.id || item.id_photo,
                        id: item.idPhoto || item.id || item.id_photo,
                        url: item.urls?.urlPreview || item.url,
                        originalUrl: item.urls?.urlOriginal || item.originalUrl || item.url,
                        originalName: item.filename || item.originalName || '图片',
                        fileCateType: 1,
                        isClicked: false,
                        status: status || 'approved', // 确保 status 始终存在
                        tags: Array.isArray(item.tags) ? item.tags.map(tag => typeof tag === 'string' ? { code: tag } : tag) : [],
                    };
                }).filter(Boolean); // 过滤掉 null 值
                
                if (isLoadMore) {
                    this.photoList = this.photoList.concat(newPhotos);
                } else {
                    this.photoList = newPhotos;
                    this.previewImageList = newPhotos.map(item => item.originalUrl);
                }
                
                this.cursor = photosData.cursor || '';
                this.isNoMore = newPhotos.length < this.limit;
                
            } catch (error) {
                console.error('加载照片失败:', error);
                this.$message.error('加载照片失败');
            } finally {
                this.isLoading = false;
            }
        },
        
        // 加载更多
        loadMore() {
            if (!this.isLoading && !this.isNoMore) {
                this.loadPhotos(true);
            }
        },
        
        // 刷新数据
        refresh() {
            this.cursor = '';
            this.isNoMore = false;
            this.photoList = [];
            this.loadPhotos();
        },
        
        // 选择分类
        selectStatus(value) {
            this.selectedCategory = value;
        },
        
        // 切换选择状态
        toggleSelect(item) {
            if (!item) return;
            this.$set(item, 'isClicked', !item.isClicked);
        },
        
        // 是否显示标签
        getIsShowTag(item) {
            if (!item) return false;
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                return item.tags && Array.isArray(item.tags) && item.tags.length > 0;
            }
            return false;
        },
        
        // 获取标签图标
        getTagIcon(item) {
            if (!item) return '';
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '');
                    switch (tagCode) {
                        case 'special_plus':
                            return require('@/assets/svg/special+.svg');
                        case 'pre_special':
                            return require('@/assets/svg/special-.svg');
                        case 'special':
                            return require('@/assets/svg/special.svg');
                        default:
                            return '';
                    }
                }
            }
            return '';
        },
        
        // 获取标签描述
        getTagDescription(item) {
            if (!item) return '';
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '');
                    switch (tagCode) {
                        case 'special_plus':
                            return '特殊+内容只能发送给某些用户，并且只能以独家帖子或邮件的形式发送';
                        case 'pre_special':
                            return 'AI预设';
                        case 'special':
                            return '特殊内容只能发送给某些用户，并且只能通过独家帖子或邮件发送';
                        default:
                            return '';
                    }
                }
            }
            return '';
        },
        
        // 确认添加
        async confirmAdd() {
            const selectedPhotos = this.photoList.filter(item => item && item.isClicked);
            
            if (selectedPhotos.length === 0) {
                this.$message.warning('请选择要添加的图片');
                return;
            }
            
            this.addingPhotos = true;
            
            try {
                // 新接口返回的数据中，照片ID可能在 idPhoto 或 id 字段中
                const photoIds = selectedPhotos
                    .map(item => item.idPhoto || item.id || item.id_photo)
                    .filter(id => id != null && id !== undefined);
                
                await addPhotosToAlbum({
                    idsAlbums: [this.albumId],
                    idsPhotos: photoIds
                });
                
                this.$message.success(`成功添加 ${selectedPhotos.length} 张图片到分类"${this.albumName}"`);
                this.$emit('photos-added', selectedPhotos);
                this.closeModal();
                
            } catch (error) {
                console.error('添加图片到分类失败:', error);
                this.$message.error('添加图片失败');
            } finally {
                this.addingPhotos = false;
            }
        },
        
        // 关闭弹窗
        closeModal() {
            this.$emit('close');
        },
        
        // 重置数据
        resetData() {
            this.photoList = [];
            this.previewImageList = [];
            this.selectedCategory = 'all_available';
            this.cursor = '';
            this.isNoMore = false;
            this.isLoading = false;
            this.addingPhotos = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.add-photos-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10002;
    animation: fadeIn 0.3s ease;
}

.add-photos-modal {
    background: #fff;
    border-radius: 12px;
    width: 800px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    display: flex;
    flex-direction: column;
}

.modal-header {
    background: linear-gradient(135deg, #67c23a 0%, #5daf34 100%);
    color: #fff;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #fff;
    }
    
    .modal-close {
        background: none;
        border: none;
        color: #fff;
        font-size: 18px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.3s ease;
        
        &:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: scale(1.1);
        }
    }
}

.modal-body {
    padding: 24px;
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.status-filter-btns {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .status-btn {
        padding: 8px 20px;
        border: 2px solid #e4e7ed;
        border-radius: 20px;
        background: #fff;
        color: #606266;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

        &:hover {
            border-color: #67c23a;
            color: #67c23a;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(103, 194, 58, 0.15);
        }

        &.selected {
            background: linear-gradient(135deg, #67c23a 0%, #5daf34 100%);
            border-color: #67c23a;
            color: #fff;
            box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
            transform: translateY(-1px);
        }
    }
}

.photo-list {
    flex: 1;
    height: 100%;
    width: 100%;
    display: block;
    padding: 0;
    margin: 0;
    list-style: none;
    overflow-y: auto;

    .photo-item {
        position: relative;
        display: inline-block;
        margin: 3px;
        padding: 10px;
        vertical-align: top;
        box-sizing: border-box;
        text-align: center;
        border: 1px solid transparent;
        transition: all 0.2s;

        &.is-selected {
            border: 1px solid #2499FF;
            border-radius: 8px;
        }

        .image-container {
            width: 150px;
            height: 150px;
            position: relative;
            display: inline-block;

            ::v-deep .imagePreview {
                width: 150px;
                height: 150px;
                display: block;

                .preview-image {
                    width: 150px;
                    height: 150px;
                    object-fit: contain;
                    border-radius: 4px;
                }
            }

            .media-tag {
                position: absolute;
                bottom: 8px;
                left: 8px;
                background: rgba(36, 153, 255, 0.8);
                color: white;
                padding: 5px;
                border-radius: 50px;
                display: flex;
                align-items: center;
                gap: 2px;
                z-index: 10;
                transition: all 0.3s ease;
                cursor: pointer;

                .tag-icon {
                    width: 18px;
                    height: 18px;
                }

                &:hover {
                    background: rgba(36, 153, 255, 0.95);
                }
            }
        }

        .selection-indicator {
            position: absolute;
            right: 0;
            top: 2px;
        }
    }

    .loading-item,
    .no-more-item {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 40px;
        width: 100%;
        color: #999;
        font-size: 14px;

        .loading-icon {
            width: 30px;
            height: 30px;
            animation: rotate360 1s linear infinite;
        }
    }
}

.loading-item,
.no-more-item,
.empty-state {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 200px;
    color: #999;
    font-size: 14px;

    .loading-icon {
        width: 30px;
        height: 30px;
        animation: rotate360 1s linear infinite;
        margin-bottom: 10px;
    }
}

.empty-state {
    i {
        margin-bottom: 10px;
    }
}

.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #e4e7ed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8f9fa;

    .selected-info {
        color: #606266;
        font-size: 14px;
    }

    .action-buttons {
        display: flex;
        gap: 12px;
    }
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { 
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
    to { 
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes rotate360 {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
